/**
 * Admin → Sourcing Teams tab — feature E05-F05-T04.
 *
 * 1:1 port of the legacy `dashboard.js` sourcing UI (loadSourcingTeams /
 * renderSourcingTeams / openSrcTeamDialog / _addSrcTeamMember / saveSrcTeam /
 * deleteSrcTeam) + `index.html` #admin-content-srcteams. It is the admin Settings
 * tab that LISTS sourcing teams as cards and offers CRUD; each team carries a
 * dynamic list of { name, email } members rendered as chips.
 *
 * Card (renderSourcingTeams, T04 acceptance #1):
 *   - heading: `<name> (<N> members)`;
 *   - one chip per member with the legacy `Name <email>` label;
 *   - Edit + Delete actions;
 *   - empty state: "No sourcing teams configured".
 *
 * Dialog (openSrcTeamDialog / saveSrcTeam, T04 acceptance #2):
 *   - Team Name * + a dynamic Members editor (Add member appends a blank
 *     name/email row; the × removes a row);
 *   - Save collects ONLY rows where BOTH name and email are non-empty
 *     (collectMembers, teamsModel) — empty rows are dropped;
 *   - a blank name shows the toast "Name is required" (legacy showNotification).
 *
 * Delete is a SOFT delete (the API flips is_active=false) — the behavioural
 * difference from the recruiter Teams tab (HARD delete). Transport:
 * useSourcingTeams / useCreateSourcingTeam / useUpdateSourcingTeam /
 * useDeleteSourcingTeam (sourcingTeamsApi.ts) → the shared apiClient.
 *
 * The card/chip/dialog rules live in the pure `teamsModel.ts` (unit-tested) so the
 * rendering can never drift from the e2e expectations.
 */
import { useState } from 'react';
import type { SourcingTeamDTO } from '@eer/shared';
import { SOURCING_TEAM_NAME_REQUIRED_MESSAGE } from '@eer/shared';
import { Dialog, DialogFooter, useToast } from '../ui/index.js';
import {
  collectMembers,
  memberChipLabel,
  memberCountLabel,
  type MemberDraft,
} from './teamsModel.js';
import { errorMessage } from '../api/client.js';
import {
  useSourcingTeams,
  useCreateSourcingTeam,
  useUpdateSourcingTeam,
  useDeleteSourcingTeam,
} from './sourcingTeamsApi.js';

const FIELD_CLASS =
  'w-full rounded border border-border px-3 py-2 text-sm focus:border-primary focus:outline-none';
const MEMBER_FIELD_CLASS =
  'flex-1 rounded border border-border px-2 py-1 text-xs focus:border-primary focus:outline-none';

// ---------------------------------------------------------------------------
// Add / Edit dialog (#srcteam-overlay)
// ---------------------------------------------------------------------------

interface SourcingTeamDialogProps {
  /** The team being edited, or null for an Add. */
  readonly team: SourcingTeamDTO | null;
  readonly onClose: () => void;
}

function SourcingTeamDialog({ team, onClose }: SourcingTeamDialogProps): React.JSX.Element {
  const toast = useToast();
  const createTeam = useCreateSourcingTeam();
  const updateTeam = useUpdateSourcingTeam();
  const [name, setName] = useState(() => team?.name ?? '');
  const [members, setMembers] = useState<MemberDraft[]>(() =>
    (team?.members ?? []).map((m) => ({ name: m.name, email: m.email })),
  );

  const isEdit = team != null;
  const pending = createTeam.isPending || updateTeam.isPending;

  function addMember(): void {
    setMembers((prev) => [...prev, { name: '', email: '' }]);
  }

  function removeMember(index: number): void {
    setMembers((prev) => prev.filter((_, i) => i !== index));
  }

  function patchMember(index: number, key: keyof MemberDraft, value: string): void {
    setMembers((prev) => prev.map((m, i) => (i === index ? { ...m, [key]: value } : m)));
  }

  function handleSave(): void {
    const trimmed = name.trim();
    // Legacy: `if (!name) { showNotification('Name is required', 'error'); return; }`
    if (!trimmed) {
      toast.error(SOURCING_TEAM_NAME_REQUIRED_MESSAGE);
      return;
    }
    // Only rows with BOTH a non-empty name and email survive (acceptance #2).
    const cleaned = collectMembers(members);
    const onSuccess = (): void => {
      toast.success('Sourcing team saved');
      onClose();
    };
    const onError = (error: unknown): void => {
      toast.error(errorMessage(error, 'Save failed'));
    };

    if (isEdit && team) {
      updateTeam.mutate(
        { id: team.id, patch: { name: trimmed, members: cleaned } },
        { onSuccess, onError },
      );
    } else {
      createTeam.mutate({ name: trimmed, members: cleaned }, { onSuccess, onError });
    }
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title={isEdit ? 'Edit Sourcing Team' : 'Add Sourcing Team'}
      maxWidth="lg"
      testId="srcteam-dialog"
      footer={
        <DialogFooter
          onCancel={onClose}
          onConfirm={handleSave}
          confirmLabel="Save"
          confirmDisabled={pending}
          testId="srcteam-dialog-footer"
        />
      }
    >
      <div className="grid gap-4">
        <label htmlFor="srcteam-name" className="block">
          <span className="mb-1 block text-xs font-medium text-text-strong">Team Name *</span>
          <input
            id="srcteam-name"
            data-testid="srcteam-name"
            type="text"
            className={FIELD_CLASS}
            value={name}
            placeholder="e.g. Team EMEA"
            onChange={(e) => setName(e.target.value)}
          />
        </label>

        <div>
          <span className="mb-1 block text-xs font-medium text-text-strong">Members</span>
          <div data-testid="srcteam-members" className="grid gap-1">
            {members.map((m, i) => (
              <div key={i} data-testid="srcteam-member-row" className="flex gap-2">
                <input
                  data-testid="srcteam-member-name"
                  type="text"
                  className={MEMBER_FIELD_CLASS}
                  value={m.name}
                  placeholder="Name"
                  onChange={(e) => patchMember(i, 'name', e.target.value)}
                />
                <input
                  data-testid="srcteam-member-email"
                  type="text"
                  className={MEMBER_FIELD_CLASS}
                  value={m.email}
                  placeholder="email@eer.com"
                  onChange={(e) => patchMember(i, 'email', e.target.value)}
                />
                <button
                  type="button"
                  data-testid="srcteam-member-remove"
                  onClick={() => removeMember(i)}
                  className="px-1 text-xs text-red-400 hover:text-red-600"
                  aria-label="Remove member"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
          <button
            type="button"
            data-testid="srcteam-add-member"
            onClick={addMember}
            className="mt-1 text-xs text-primary hover:underline"
          >
            + Add member
          </button>
        </div>
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Card
// ---------------------------------------------------------------------------

interface SourcingTeamCardProps {
  readonly team: SourcingTeamDTO;
  readonly onEdit: (team: SourcingTeamDTO) => void;
  readonly onDelete: (team: SourcingTeamDTO) => void;
}

function SourcingTeamCard({ team, onEdit, onDelete }: SourcingTeamCardProps): React.JSX.Element {
  return (
    <div
      data-testid="srcteam-card"
      data-team-id={team.id}
      className="mb-3 rounded-lg border border-border bg-white p-4"
    >
      <div className="mb-2 flex items-center justify-between">
        <h3 className="font-bold text-text-strong">
          <span data-testid="srcteam-card-name">{team.name}</span>{' '}
          <span data-testid="srcteam-card-count" className="text-xs font-normal text-gray-400">
            {memberCountLabel(team.members.length)}
          </span>
        </h3>
        <div className="flex gap-3">
          <button
            type="button"
            data-testid="srcteam-card-edit"
            onClick={() => onEdit(team)}
            className="text-xs text-primary hover:underline"
            aria-label="Edit sourcing team"
            title="Edit sourcing team"
          >
            <i className="fa-solid fa-pen mr-1" aria-hidden />
            Edit
          </button>
          <button
            type="button"
            data-testid="srcteam-card-delete"
            onClick={() => onDelete(team)}
            className="text-xs text-red-500 hover:underline"
            aria-label="Delete sourcing team"
            title="Delete sourcing team"
          >
            <i className="fa-solid fa-trash mr-1" aria-hidden />
            Delete
          </button>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        {team.members.map((m, i) => (
          <span
            key={`${m.email}-${i}`}
            data-testid="srcteam-member-chip"
            className="rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-600"
          >
            {memberChipLabel(m)}
          </span>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab
// ---------------------------------------------------------------------------

/**
 * The Sourcing Teams admin tab. Lists sourcing teams as cards (server orders by
 * name) and offers Add / Edit / Delete (SOFT). Empty list → "No sourcing teams
 * configured" (T04 acceptance #1).
 */
export function SourcingTeamsTab(): React.JSX.Element {
  const query = useSourcingTeams();
  const deleteTeam = useDeleteSourcingTeam();
  const toast = useToast();
  const [editing, setEditing] = useState<SourcingTeamDTO | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const teams = query.data ?? [];

  function openAdd(): void {
    setEditing(null);
    setDialogOpen(true);
  }

  function openEdit(team: SourcingTeamDTO): void {
    setEditing(team);
    setDialogOpen(true);
  }

  function closeDialog(): void {
    setDialogOpen(false);
    setEditing(null);
  }

  function handleDelete(team: SourcingTeamDTO): void {
    deleteTeam.mutate(team.id, {
      onSuccess: () => toast.success('Sourcing team deleted'),
      onError: (error) => toast.error(errorMessage(error, 'Delete failed')),
    });
  }

  if (query.isLoading) {
    return (
      <div data-testid="srcteams-loading" className="py-4 text-center text-gray-400">
        Loading…
      </div>
    );
  }

  if (query.isError) {
    return (
      <div data-testid="srcteams-error" className="py-4 text-center text-red-500">
        {errorMessage(query.error, 'Failed to load sourcing teams')}
      </div>
    );
  }

  return (
    <div data-testid="srcteams-tab">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-medium text-text-strong">Sourcing Teams</h2>
        <button
          type="button"
          data-testid="srcteams-add"
          onClick={openAdd}
          className="rounded bg-primary px-3 py-1.5 text-xs font-medium text-white hover:opacity-90"
        >
          Add sourcing team
        </button>
      </div>

      {teams.length === 0 ? (
        <p data-testid="srcteams-empty" className="py-4 text-center text-sm text-gray-400">
          No sourcing teams configured
        </p>
      ) : (
        <div data-testid="srcteams-list">
          {teams.map((t) => (
            <SourcingTeamCard key={t.id} team={t} onEdit={openEdit} onDelete={handleDelete} />
          ))}
        </div>
      )}

      {dialogOpen ? <SourcingTeamDialog team={editing} onClose={closeDialog} /> : null}
    </div>
  );
}

export default SourcingTeamsTab;
